import { useScrollContent } from '@exam-management/common/hooks';
import {
  useCreateInDevelopmentQuestionMutation,
  GetQuestionCreationTaskByIdDocument,
} from '@exam-management/graphql';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { Button, Flex } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { DEFAULT_FORM_VALUES } from './constants';
import type { SingleQuestionCreationFormInput } from './single-question-creation.schema';
import { parseQuestionFormDataForApi } from './utils';

export default function SingleQuestionCreationFormActions() {
  const { showSnackbar } = useSnackbar();

  const { task } = useQuestionCreationTaskDetails();
  const { scrollTo } = useScrollContent();
  const formContext = useFormContext<SingleQuestionCreationFormInput>();

  const { formState } = formContext;
  const { isSubmitSuccessful } = formState;

  const isLoading = false;
  const [createInDevelopmentQuestion] = useCreateInDevelopmentQuestionMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to Save Question',
        variant: 'error',
      });
    },
  });

  const createSingleQuestion = async (data: SingleQuestionCreationFormInput) => {
    const qBankStructureLevelItemId = data.location.SUB_TOPIC?.value as string;
    const qBankStructureId = task.metadata?.requirement.qBankStructure?.id;

    if (!qBankStructureLevelItemId || !qBankStructureId)
      throw Error('Invalid qbs id and level item id');

    const payload = parseQuestionFormDataForApi(data, parseContentForApi);

    const result = await createInDevelopmentQuestion({
      variables: {
        input: {
          qBankStructureLevelItemId,
          refQBankStructureId: qBankStructureId,
          data: payload,
        },
        taskId: task.id,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId: task.id,
            },
          },
        },
      ],
      onCompleted: () => {
        scrollTo({ x: 0, y: 0 });
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });
      },
    });

    if (result.errors) throw result.errors;
  };

  const handleShowRequiredFieldsError = () => {
    showSnackbar({
      message: 'Please complete all required fields',
      variant: 'error',
    });
  };

  const handleSaveAndCreateNew = async () => {
    await formContext.handleSubmit(async (data) => {
      await createSingleQuestion(data);
    }, handleShowRequiredFieldsError)();
  };

  useEffect(() => {
    if (isSubmitSuccessful) {
      formContext.reset({
        ...DEFAULT_FORM_VALUES,
      });
    }
  }, [isSubmitSuccessful, formContext]);

  const questionCreationCount = task.metadata?.requirement.questionCreationCount ?? 0;
  const requiredQuestionCount = task.metadata?.requirement.requiredQuestionCount ?? 0;

  // +1 because we are creating a new question
  const shouldAllowSaveAndCreateNew = questionCreationCount + 1 < requiredQuestionCount;

  return (
    <Flex sx={{ gap: 4 }}>
      <Button
        type='submit'
        disabled={formContext.formState.isSubmitting || !formContext.formState.isDirty}
        // NOTE: This is a workaround to show loading status for only one button
        loading={formContext.formState.isSubmitting && !isLoading}
      >
        Save
      </Button>
      {shouldAllowSaveAndCreateNew ? (
        <Button
          type='button'
          color='secondary'
          variant='outlined'
          disabled={formContext.formState.isSubmitting || !formContext.formState.isDirty}
          loading={isLoading}
          onClick={handleSaveAndCreateNew}
        >
          Save & Create New
        </Button>
      ) : null}
    </Flex>
  );
}
